// Company workspace access control and settings contracts (Employer Phase 1).

/**
 * Company membership roles. `CompanyMember.role` is an untyped string column
 * with only `'owner'` written historically — readers must normalize unknown
 * values to `'member'` (fail closed), never to `'owner'`.
 */
export type CompanyRole = 'owner' | 'admin' | 'member';

export const COMPANY_PERMISSIONS = [
  'page.edit',
  'job.manage',
  'job.publish',
  'job.close',
  'applicant.read',
  'applicant.status',
  'applicant.message',
  'settings.manage',
  'share',
] as const;

export type CompanyPermission = (typeof COMPANY_PERMISSIONS)[number];

export const COMPANY_ROLE_PERMISSIONS: Record<
  CompanyRole,
  readonly CompanyPermission[]
> = {
  owner: COMPANY_PERMISSIONS,
  admin: COMPANY_PERMISSIONS,
  member: ['page.edit', 'applicant.read'],
};

/** Phase 1 public social platforms for company pages (PRD §6.2). */
export const COMPANY_SOCIAL_PLATFORMS = [
  'linkedin',
  'instagram',
  'youtube',
  'whatsapp',
  'tiktok',
  'x',
  'facebook',
] as const;

export type CompanySocialPlatform = (typeof COMPANY_SOCIAL_PLATFORMS)[number];

/** Normalized destination URLs keyed by platform — never presence-only booleans. */
export type CompanySocialLinks = Partial<Record<CompanySocialPlatform, string>>;

export type CompanyPageLanguage = 'en-AE' | 'ar-AE' | 'en' | 'ar';

/**
 * Typed contract for `Company.seoSettings` (previously free-form Json).
 * `index` defaults to true when absent — the public renderer treats
 * `seo.index !== false` as indexable.
 */
export interface CompanySeoSettings {
  /** Search-result title, max 60 characters. */
  searchTitle?: string;
  /** Search-result description, max 160 characters. */
  searchDescription?: string;
  /** Search-engine indexing toggle; absent means indexable. */
  index?: boolean;
  socialTitle?: string;
  socialDescription?: string;
  /** When true, social sharing reuses searchTitle/searchDescription. */
  useSearchTextForSocial?: boolean;
  /** Absolute URL of the social share image. */
  socialImage?: string;
  language?: CompanyPageLanguage;
}
